/**
 * MobileTopBar Component
 *
 * Liquid glass top bar for mobile screens.
 * Contains the app logo, coin balance badge, and profile avatar that opens
 * a bottom sheet with user info, coins, and actions.
 *
 * Features:
 * - Liquid glass design matching the bottom nav
 * - App logo on the left
 * - Coin balance badge in the center-right
 * - Profile avatar on the right (opens bottom sheet)
 * - Bottom sheet with user info, coin balance, platform manager, feedback
 * - Gesture-based drag-to-close on the profile sheet
 * - Hidden on desktop (md:hidden)
 */

import {
	Coins,
	LogOut,
	ShieldCheck,
	MessageSquare,
	Settings,
	Sparkles,
	Crown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/contexts/auth.context";
import { SystemRole } from "@/lib/auth/auth.types";
import { useCoinBalance } from "@/lib/coin/coin.store";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { SubscriptionService } from "@/lib/subscription/subscription.service";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { FeedbackModal } from "@/components/feedbackModal.component";
import { useBottomSheetGesture } from "@/components/player/hooks/useBottomSheetGesture.hook";

export function MobileTopBar() {
	const navigate = useNavigate();
	const { t } = useTranslation("common");
	const { user, logout, isAuthenticated } = useAuth();
	const { balance: coinBalance, isLoading: isLoadingBalance } =
		useCoinBalance();
	const { subscriptionStatus, currentPlanId } = useSubscription();
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

	// Fetch plans to resolve currentPlanId -> plan name
	const { data: plans } = useQuery({
		queryKey: ["subscription-plans"],
		queryFn: () => SubscriptionService.getPlans(),
		staleTime: 1000 * 60 * 30, // cache for 30 min
	});

	const isPaidPlan =
		subscriptionStatus &&
		subscriptionStatus !== "NONE" &&
		subscriptionStatus !== "CANCELED" &&
		subscriptionStatus !== "INCOMPLETE_EXPIRED";

	const currentPlanName = plans?.find((p) => p.id === currentPlanId)?.name;

	// Setup gesture for drag-to-close on profile sheet
	const { dragState, bind } = useBottomSheetGesture({
		onClose: () => setIsProfileOpen(false),
		closeThreshold: 150,
		disabled: !isProfileOpen,
	});

	const getUserInitials = () => {
		if (!user) return "U";
		if (user.displayName) {
			const nameParts = user.displayName.trim().split(/\s+/);
			if (nameParts.length >= 2) {
				return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
			}
			return nameParts[0].substring(0, 2).toUpperCase();
		}
		if (user.email) {
			return user.email.substring(0, 2).toUpperCase();
		}
		return "U";
	};

	const isPlatformManager = user?.systemRoles?.includes(
		SystemRole.PLATFORM_MANAGER,
	);

	return (
		<>
			<div className="fixed top-2 left-3 right-3 z-40 md:hidden liquid-glass-light rounded-[28px]">
				<div className="grid grid-cols-3 items-center w-full py-1.5 px-3">
					{/* Left — settings */}
					<div className="flex items-center justify-start">
						{isAuthenticated && (
							<Sheet open={isProfileOpen} onOpenChange={setIsProfileOpen}>
								<SheetTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="[&_svg]:size-4 size-7"
										aria-label={t("mobileNav.profile")}
									>
										<Settings className="text-muted-foreground" />
									</Button>
								</SheetTrigger>
								<SheetContent
									side="bottom"
									className="h-auto rounded-t-3xl border-t border-foreground/[0.06] pb-8 liquid-glass-bold !rounded-b-none"
									style={{
										transform: dragState.isDragging
											? `translateY(${dragState.y}px)`
											: undefined,
										opacity: dragState.isDragging ? dragState.opacity : 1,
										transition: dragState.isDragging
											? "none"
											: "transform 0.3s ease-out, opacity 0.3s ease-out",
									}}
								>
									{/* Drag handle */}
									<div
										className="flex flex-col items-center pt-2 pb-2 cursor-grab active:cursor-grabbing touch-none"
										{...bind()}
									>
										<div className="w-12 h-1.5 bg-foreground/15 rounded-full mb-4" />
									</div>

									<div className="space-y-6 px-1">
										{/* User Info Card */}
										<div className="flex items-center gap-4 p-4 liquid-glass-surface rounded-2xl border border-foreground/[0.06]">
											<Avatar className="size-16 ring-2 ring-orange-500/30 shadow-lg">
												<AvatarFallback className="bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold text-lg">
													{getUserInitials()}
												</AvatarFallback>
											</Avatar>
											<div className="flex flex-col min-w-0 flex-1">
												<span className="text-base font-bold text-foreground truncate">
													{user?.displayName ||
														user?.email?.split("@")[0] ||
														"User"}
												</span>
												<span className="text-sm text-muted-foreground truncate">
													{user?.email || ""}
												</span>
											</div>
											<Button
												onClick={() => {
													logout();
													setIsProfileOpen(false);
												}}
												className="p-2 h-10 gap-1 rounded-full hover:bg-destructive/10 transition-colors border-destructive/10 flex-col mr-6"
												aria-label={t("mobileNav.logout")}
												variant="ghost"
												size="iconLg"
											>
												<LogOut className="text-destructive" />
												<span className="text-destructive">
													{t("mobileNav.logout")}
												</span>
											</Button>
										</div>

										{/* Coin Balance Card */}
										<button
											type="button"
											onClick={() => {
												navigate("/pricing");
												setIsProfileOpen(false);
											}}
											className="w-full relative liquid-glass-surface rounded-2xl p-5 overflow-hidden cursor-pointer transition-all active:scale-[0.98] border border-orange-500/20"
										>
											<div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/8 rounded-full blur-3xl" />
											<div className="absolute bottom-0 left-0 w-24 h-24 bg-amber-500/8 rounded-full blur-2xl" />
											<div className="relative flex items-center justify-between">
												<div className="flex items-center gap-3">
													<div className="p-2.5 liquid-glass-surface rounded-xl">
														<Coins className="h-6 w-6 text-orange-500" />
													</div>
													<div className="flex flex-col">
														<span className="text-xs text-muted-foreground font-medium">
															{t("mobileNav.yourCoins")}
														</span>
														{isLoadingBalance ? (
															<span className="text-2xl font-bold text-orange-500">
																...
															</span>
														) : coinBalance !== null ? (
															<span className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
																{coinBalance.toLocaleString()}
															</span>
														) : (
															<span className="text-2xl font-bold text-orange-500">
																{t("mobileNav.notAvailable")}
															</span>
														)}
													</div>
												</div>
											</div>
										</button>

										{/* Action Buttons */}
										<div className="space-y-2 pt-2">
											<Button
												variant="ghost"
												size="lg"
												className="w-full justify-start h-14 text-base hover:bg-foreground/[0.06] rounded-xl"
												onClick={() => {
													navigate("/settings");
													setIsProfileOpen(false);
												}}
											>
												<Settings className="mr-3 h-5 w-5" />
												<span>{t("mobileNav.settings")}</span>
											</Button>
											{isPlatformManager && (
												<Button
													variant="ghost"
													size="lg"
													className="w-full justify-start h-14 text-base hover:bg-foreground/[0.06] rounded-xl"
													onClick={() => {
														navigate("/platform-manager");
														setIsProfileOpen(false);
													}}
												>
													<ShieldCheck className="mr-3 h-5 w-5" />
													<span>{t("mobileNav.platformManager")}</span>
												</Button>
											)}
											<Button
												variant="ghost"
												size="lg"
												className="w-full justify-start h-14 text-base hover:bg-foreground/[0.06] rounded-xl"
												onClick={() => {
													setIsFeedbackModalOpen(true);
													setIsProfileOpen(false);
												}}
											>
												<MessageSquare className="mr-3 h-5 w-5" />
												<span>{t("mobileNav.sendFeedback")}</span>
											</Button>
										</div>
									</div>
								</SheetContent>
							</Sheet>
						)}
					</div>

					{/* Center — logo */}
					<div className="flex justify-center">
						<img
							src="/podhive-wortbildmarke-banner.png"
							alt="Podhive Banner"
							className="h-5 object-cover"
						/>
					</div>

					{/* Right — upgrade / plan */}
					<div className="flex items-center justify-end">
						{isPaidPlan ? (
							<button
								type="button"
								onClick={() => navigate("/pricing")}
								className="flex items-center gap-1 liquid-glass-surface border border-foreground/10 rounded-full px-2 py-0.5 cursor-pointer transition-all active:scale-95"
							>
								<Crown className="h-3 w-3 text-orange-500" />
								<span className="text-[11px] font-semibold text-foreground">
									{currentPlanName ?? t("mobileNav.plan")}
								</span>
							</button>
						) : (
							<button
								type="button"
								onClick={() => navigate("/pricing")}
								className="flex items-center gap-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full px-2 py-0.5 cursor-pointer transition-all active:scale-95 shadow-sm"
							>
								<Sparkles className="h-3 w-3 text-white" />
								<span className="text-[11px] font-semibold text-white">
									{t("mobileNav.upgrade")}
								</span>
							</button>
						)}
					</div>
				</div>
			</div>

			{/* Feedback Modal */}
			<FeedbackModal
				open={isFeedbackModalOpen}
				onOpenChange={setIsFeedbackModalOpen}
			/>
		</>
	);
}
